import {
    CheckBox,
    Column,
    Container,
    cssVar,
    Divider,
    Document,
    DropDown,
    GenericModal,
    Grid,
    Header,
    IconButton,
    Label,
    PrimaryButton,
    RenewalSmallIcon,
    Row,
    TextArea,
    UploadButton,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import Helper from '../../../helpers';

import ActionsMenuConfig from './ActionsMenu/configs';

const CustomContainer = styled(Container)`
    height: 10rem;
    padding: 0 1rem;
    border: 1px solid ${cssVar.colorStrokeDark};
    border-radius: 5px;
    overflow-y: auto;
`;

const StyledTextArea = styled(TextArea)`
    border: 1px solid ${cssVar.colorStrokeDark};
    border-radius: ${cssVar.radiusSmall};
`;

const productCodeMap = {};

const CheckmarkIcon = ({ color }) => (
    <svg width='33' height='32' viewBox='0 0 33 32' fill={color} xmlns='http://www.w3.org/2000/svg'>
        <path d='M32.641 1.25231C32.3463 0.854486 31.9485 0.618738 31.4475 0.545067C30.2835 0.48613 28.8101 1.09023 27.0272 2.35738C24.1983 4.67066 21.3693 7.26389 18.5403 10.1371L11.269 18.2704C7.92427 16.517 5.29421 15.4709 3.37875 15.132C2.21474 15.0141 1.44856 15.2941 1.08021 15.9718C0.682381 16.6496 0.689748 17.2169 1.10231 17.6737C3.35665 19.7217 5.33104 21.954 7.02548 24.3704C8.71992 26.7721 10.1786 29.1295 11.4016 31.4428C11.6226 31.7522 11.8804 31.8996 12.1751 31.8848C12.4698 31.8554 12.7276 31.6712 12.9487 31.3323C14.702 27.0299 16.7354 22.8969 19.0486 18.9334C20.581 16.3255 22.1428 14.0638 23.7341 12.1483C24.8834 10.5865 26.1579 9.08357 27.5577 7.63962L32.0001 3.32984C32.0001 3.31511 32.0074 3.30774 32.0222 3.30774C32.0369 3.27827 32.059 3.2488 32.0885 3.21934C32.2653 2.98359 32.42 2.76994 32.5526 2.5784C32.8326 2.0627 32.862 1.62067 32.641 1.25231Z' />
    </svg>
);

const ProfileDocuments = (props) => {
    const alignments = useAlignments();
    const sizes = useSizes();
    const intl = useIntl();

    const [generateFileCode, setGenerateFileCode] = useState({ code: 'GENERATEPDF', value: 'Generate PDF form' });
    const [showSendEmailModal, setShowSendEmailModal] = useState(false);
    const [showGenerateDocumentModal, setShowGenerateDocumentModal] = useState(false);
    const [showSharingModal, setShowSharingModal] = useState(false);
    const [sharedDocuments, setSharedDocuments] = useState({});
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const [showDocumentsForRegenerationModal, setShowDocumentsForRegenerationModal] = useState(false);
    const [selectedDocumentsForRegeneration, setSelectedDocumentsForRegeneration] = useState([]);
    const [selectedRegenerateReason, setSelectedRegenerateReason] = useState(null);

    const searchParams = new URLSearchParams(window.location.search);
    const entityFromSearch = searchParams.get('entity');
    const entity = props.entity || entityFromSearch;

    const { isMobile: mobile } = useScreen();
    const allowUpload =
        props.context.session.userType === 'administrator' || props.context.session.userType === 'broker';

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        props.document.doReduceDocumentQueryElement({ code: 'relatedEntity', value: entity });
    }, []);

    // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on listed deps
    useEffect(() => {
        props.document.doQueryDocuments();
    }, [props.riskChanged]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on listed deps
    useEffect(() => {
        if (
            !props.document.shareableDocuments &&
            (showSharingModal || showSendEmailModal || showDocumentsForRegenerationModal)
        ) {
            props.document.doQueryShareableDocuments(entity);
        }
    }, [showSharingModal, showSendEmailModal, showDocumentsForRegenerationModal]);

    const product = props.policy?.product;
    const references = productCodeMap[product];

    const displayButton = (Configs, type, role, product) => {
        return Configs.some((obj) => obj.type === type && obj.role.includes(role) && obj.products.includes(product));
    };

    const displayRegenerateDocumentsButton =
        !props.allowedit &&
        displayButton(ActionsMenuConfig, 'displayRegenerateDocumentsButton', props.context.session.userType, product);

    const getDocuments = (type) => {
        let documents = [];
        if (props.document?.documents?.length > 0 && props.document.documents.some((x) => x.type === type)) {
            documents = props.document.documents.filter((x) => x.type === type);
        }
        return documents;
    };

    const generateDocumentBtnClick = () => {
        setShowGenerateDocumentModal(true);
    };

    const sendEmailBtnClick = () => {
        setShowSendEmailModal(true);
    };

    const handleShareBtnClick = () => {
        setShowSharingModal(true);
    };

    const handleRegenerateBtnClick = () => {
        setShowDocumentsForRegenerationModal(true);
    };

    const handleSharingApply = () => {
        const documentSharingProperties = Object.keys(sharedDocuments).map((guid) => ({
            documentGuid: guid,
            isShared: sharedDocuments[guid],
        }));

        props.document.doShareUnshareDocuments({
            policyTransactionGuid: props.entity,
            documentSharingProperties,
        });

        props.document.doQueryDocuments();
        handleSharingModalClose();
        window.location.reload();
    };

    const handleGenerateDocument = () => {
        props.doExecutePolicyAction({ code: generateFileCode.code });
        setShowGenerateDocumentModal(false);
    };

    const handleSendEmailModalClose = () => {
        setShowSendEmailModal(false);
        setSelectedTemplate(null);
        setSelectedDocuments([]);
    };

    const handleGenerateDocumentModalClose = () => {
        setShowGenerateDocumentModal(false);
        setGenerateFileCode('');
    };

    const handleSharingModalClose = () => {
        setSharedDocuments({});
        setShowSharingModal(false);
    };

    const handleRegenerateDocumentsModalClose = () => {
        setShowDocumentsForRegenerationModal(false);
        setSelectedRegenerateReason(null);
        setSelectedDocumentsForRegeneration([]);
    };

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
    };

    const handleDocumentSelect = (documentId) => {
        setSelectedDocuments((prevState) => {
            if (prevState.includes(documentId)) {
                return prevState.filter((id) => id !== documentId);
            }
            return [...prevState, documentId];
        });
    };

    const handleRegeneratedDocumentsSelect = (documentId) => {
        setSelectedDocumentsForRegeneration((prevState) => {
            if (prevState.includes(documentId)) {
                return prevState.filter((id) => id !== documentId);
            }
            return [...prevState, documentId];
        });
    };

    const handleRegenerateDocumentsReasonSelect = (reason) => {
        setSelectedRegenerateReason(reason);
    };

    const handleDocumentShare = (guid, status) => {
        setSharedDocuments((prevState) => ({
            ...prevState,
            [guid]: status,
        }));
    };

    const handleSendEmail = () => {
        props.doSendEmailRequest({
            policyTransactionGuid: props.entity,
            templateType: selectedTemplate.code,
            documentAttachmentGuids: selectedDocuments,
        });

        handleSendEmailModalClose();
    };

    const handleRegenerateDocuments = () => {
        props.doRegenerateDocuments({
            regenerateDocumentDto: {
                policyTransactionGuid: props.entity,
                documentGuids: selectedDocumentsForRegeneration,
                reason: selectedRegenerateReason,
            },
        });

        handleRegenerateDocumentsModalClose();
    };

    const policyDocuments = getDocuments('policyDocument');
    const policyAttachments = getDocuments('policyAttachment');

    return (
        <Container>
            <Divider top={sizes.xl} />
            <Header
                size={sizes.lg}
                textAlign={alignments.left}
                text={<FormattedMessage id='Profile_Documents.documents' />}
            />
            <Divider top={sizes.lg} />
            <Header
                size={sizes.md}
                bottom={sizes.xs}
                textAlign={alignments.left}
                text={<FormattedMessage id='Profile_Documents.action' />}
            />
            <Container top={sizes.xs} bottom={sizes.sm} inline={!mobile}>
                <IconButton
                    text={<FormattedMessage id='Profile_Documents.refreshDocuments' />}
                    size={sizes.sm}
                    bottom={mobile ? sizes.sm : sizes.none}
                    icon={<RenewalSmallIcon color={cssVar.colorLightMode} />}
                    clickEvent={props.document.doQueryDocuments}
                />
                {references && (
                    <PrimaryButton
                        text={<FormattedMessage id='Profile_Documents.generateDocument' />}
                        size={sizes.sm}
                        left={mobile ? sizes.none : sizes.sm}
                        bottom={mobile ? sizes.sm : sizes.none}
                        clickEvent={generateDocumentBtnClick}
                    />
                )}
                <PrimaryButton
                    text={<FormattedMessage id='Profile_Documents.sendEmailToClient' />}
                    size={sizes.sm}
                    left={references || !mobile ? sizes.sm : sizes.none}
                    bottom={mobile ? sizes.sm : sizes.none}
                    clickEvent={sendEmailBtnClick}
                />
                <PrimaryButton
                    text={<FormattedMessage id='Profile_Documents.shareDocuments' />}
                    size={sizes.sm}
                    left={mobile ? sizes.none : sizes.sm}
                    bottom={mobile && displayRegenerateDocumentsButton ? sizes.sm : sizes.none}
                    clickEvent={handleShareBtnClick}
                />
                {displayRegenerateDocumentsButton && (
                    <PrimaryButton
                        text={<FormattedMessage id='Profile_Documents.regenerateDocuments' />}
                        size={sizes.sm}
                        left={mobile ? sizes.none : sizes.sm}
                        clickEvent={handleRegenerateBtnClick}
                    />
                )}
            </Container>
            <Grid>
                <Row nogutter>
                    <Column xs={10} md={11} nogutter>
                        <Header
                            top={sizes.xl}
                            size={mobile ? sizes.sm : sizes.md}
                            textAlign={alignments.left}
                            text={<FormattedMessage id='Profile_Documents.policyDocuments' />}
                        />
                    </Column>
                    <Column nogutter xs={2} md={1}>
                        <Header
                            top={sizes.xl}
                            size={mobile ? sizes.sm : sizes.md}
                            textAlign={alignments.left}
                            text={<FormattedMessage id='Profile_Documents.shared' />}
                        />
                    </Column>
                </Row>
                {policyDocuments?.map((document) => (
                    <Row key={`${document.displayName}-${document.id}`} nogutter>
                        <Column xs={10} md={11}>
                            <Document
                                document={document}
                                downloadEvent={props.document.doGetDocument}
                                mobile={mobile}
                            />
                        </Column>
                        <Column nogutter xs={2} md={1}>
                            <Container
                                fullSize
                                verticalAlignment={alignments.center}
                                horizontalAlignment={alignments.center}
                            >
                                {document.isShared ? <CheckmarkIcon color={cssVar.colorSuccess} /> : null}
                            </Container>
                        </Column>
                    </Row>
                ))}
            </Grid>
            <Container inline top={sizes.md}>
                {allowUpload && (
                    <UploadButton
                        text={<FormattedMessage id='Profile_Documents.upload' />}
                        changeEvent={(e) => {
                            props.document.doUpsertDocument({
                                entity: props.entity,
                                type: 'policyDocument',
                                file: e.target.files[0],
                            });
                        }}
                    />
                )}
            </Container>
            <Grid>
                <Row nogutter>
                    <Column nogutter xs={mobile ? 10 : 11}>
                        <Header
                            top={sizes.xl}
                            size={mobile ? sizes.sm : sizes.md}
                            textAlign={alignments.left}
                            text={<FormattedMessage id='Profile_Documents.policyAttachments' />}
                        />
                    </Column>
                    <Column nogutter xs={mobile ? 2 : 1}>
                        <Header
                            top={sizes.xl}
                            size={mobile ? sizes.sm : sizes.md}
                            textAlign={alignments.left}
                            text={<FormattedMessage id='Profile_Documents.shared' />}
                        />
                    </Column>
                </Row>
                {policyAttachments?.map((document) => (
                    <Row key={`${document.displayName}-${document.id}`} nogutter>
                        <Column xs={11}>
                            <Document document={document} downloadEvent={props.document.doGetDocument} />
                        </Column>
                        <Column nogutter xs={1}>
                            <Container
                                fullSize
                                verticalAlignment={alignments.center}
                                horizontalAlignment={alignments.center}
                            >
                                {document.isShared && <CheckmarkIcon color={cssVar.colorSuccess} />}
                            </Container>
                        </Column>
                    </Row>
                ))}
            </Grid>
            {allowUpload && (
                <Container inline top={sizes.md}>
                    <UploadButton
                        text={<FormattedMessage id='Profile_Documents.upload' />}
                        changeEvent={(e) => {
                            props.document.doUpsertDocument({
                                entity: props.entity,
                                type: 'policyAttachment',
                                file: e.target.files[0],
                            });
                        }}
                    />
                </Container>
            )}
            {showSendEmailModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={handleSendEmailModalClose}
                    items={
                        <React.Fragment>
                            <Header
                                text={<FormattedMessage id='Profile_Documents.sendEmailToClient' />}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            <Container>
                                <Label
                                    text={<FormattedMessage id='Profile_Documents.template' />}
                                    textAlign={alignments.left}
                                    size={sizes.lg}
                                    bottom={sizes.xs}
                                />
                                <DropDown
                                    value={selectedTemplate}
                                    references={
                                        props.reference?.references[
                                            `CustomReference|PolicyReferenceService|EmailTemplate|${props.entity}`
                                        ]
                                    }
                                    referencesEvent={() =>
                                        props.reference?.doGetReferences(
                                            `CustomReference|PolicyReferenceService|EmailTemplate|${props.entity}`,
                                        )
                                    }
                                    changeEvent={handleTemplateSelect}
                                    bottom={sizes.md}
                                />
                                {props.document?.shareableDocuments && props.document.shareableDocuments.length > 0 && (
                                    <>
                                        <Label
                                            text={<FormattedMessage id='Profile_Documents.documentsOptional' />}
                                            textAlign={alignments.left}
                                            size={sizes.lg}
                                            bottom={sizes.xs}
                                        />
                                        <CustomContainer>
                                            {props.document.shareableDocuments.map((document) => (
                                                <CheckBox
                                                    label={document.displayName}
                                                    key={document.guid}
                                                    value={selectedDocuments.includes(document.guid)}
                                                    clickEvent={() => handleDocumentSelect(document.guid)}
                                                    bottom={sizes.xs}
                                                />
                                            ))}
                                        </CustomContainer>
                                    </>
                                )}
                            </Container>
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            primary: false,
                            text: <FormattedMessage id='Profile_Documents.cancel' />,
                            disabled: false,
                            clickEvent: handleSendEmailModalClose,
                        },
                        {
                            primary: true,
                            text: <FormattedMessage id='Profile_Documents.send' />,
                            disabled: false,
                            clickEvent: () => handleSendEmail(),
                        },
                    ]}
                />
            )}
            {showGenerateDocumentModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={handleGenerateDocumentModalClose}
                    items={
                        <React.Fragment>
                            <Header
                                text={<FormattedMessage id='Profile_Documents.generateDocument' />}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            <Container>
                                <Label
                                    text={<FormattedMessage id='Profile_Documents.selectWhatToGenerate' />}
                                    textAlign={alignments.left}
                                    size={sizes.lg}
                                    bottom={sizes.xs}
                                />
                                <DropDown
                                    value={generateFileCode}
                                    references={references}
                                    changeEvent={(value) => setGenerateFileCode(value)}
                                />
                            </Container>
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            primary: false,
                            text: <FormattedMessage id='Profile_Documents.cancel' />,
                            disabled: false,
                            clickEvent: handleGenerateDocumentModalClose,
                        },
                        {
                            primary: true,
                            text: <FormattedMessage id='Profile_Documents.generate' />,
                            disabled: false,
                            clickEvent: handleGenerateDocument,
                        },
                    ]}
                />
            )}
            {showSharingModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={handleSharingModalClose}
                    items={
                        <React.Fragment>
                            <Header
                                text={<FormattedMessage id='Profile_Documents.shareDocuments' />}
                                size={sizes.md}
                                bottom={sizes.xs}
                            />
                            <Container>
                                <CustomContainer>
                                    {props.document?.shareableDocuments?.map((document) => (
                                        <CheckBox
                                            label={document.displayName}
                                            key={document.guid}
                                            value={
                                                sharedDocuments[document.guid] !== undefined
                                                    ? sharedDocuments[document.guid]
                                                    : document.isShared
                                            }
                                            clickEvent={(value) => handleDocumentShare(document.guid, value)}
                                            bottom={sizes.xs}
                                        />
                                    ))}
                                </CustomContainer>
                            </Container>
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            primary: false,
                            text: <FormattedMessage id='Profile_Documents.cancel' />,
                            disabled: false,
                            clickEvent: handleSharingModalClose,
                        },
                        {
                            primary: true,
                            text: <FormattedMessage id='Profile_Documents.apply' />,
                            disabled: Object.keys(sharedDocuments).length === 0,
                            clickEvent: handleSharingApply,
                        },
                    ]}
                />
            )}
            {showDocumentsForRegenerationModal && (
                <GenericModal
                    animate
                    visible
                    closeEvent={handleRegenerateDocumentsModalClose}
                    items={
                        <React.Fragment>
                            <Header
                                text={<FormattedMessage id='Profile_Documents.regenerateDocuments' />}
                                size={sizes.md}
                                bottom={sizes.md}
                            />
                            <Container>
                                <Label
                                    text={<FormattedMessage id='Profile_Documents.reason' />}
                                    textAlign={alignments.left}
                                    size={sizes.lg}
                                    bottom={sizes.xs}
                                />
                                <StyledTextArea
                                    text={selectedRegenerateReason}
                                    changeEvent={handleRegenerateDocumentsReasonSelect}
                                    placeholder={intl.formatMessage({
                                        id: 'Profile_Documents.Input.reasonPlaceholder',
                                    })}
                                    bottom={sizes.md}
                                />
                                {props.document?.shareableDocuments && props.document.shareableDocuments.length > 0 && (
                                    <>
                                        <Label
                                            text={<FormattedMessage id='Profile_Documents.documents' />}
                                            textAlign={alignments.left}
                                            size={sizes.lg}
                                            bottom={sizes.xs}
                                        />
                                        <CustomContainer>
                                            {props.document.shareableDocuments.map((document) => (
                                                <CheckBox
                                                    label={
                                                        <>
                                                            {document.displayName}
                                                            <br />
                                                            {Helper.FormatDateTime(document.entity.createdOn)}
                                                        </>
                                                    }
                                                    key={document.guid}
                                                    value={selectedDocumentsForRegeneration.includes(document.guid)}
                                                    clickEvent={() => handleRegeneratedDocumentsSelect(document.guid)}
                                                    bottom={sizes.xs}
                                                />
                                            ))}
                                        </CustomContainer>
                                    </>
                                )}
                            </Container>
                        </React.Fragment>
                    }
                    actionButtons={[
                        {
                            primary: false,
                            text: <FormattedMessage id='Profile_Documents.cancel' />,
                            disabled: false,
                            clickEvent: handleRegenerateDocumentsModalClose,
                        },
                        {
                            primary: true,
                            text: <FormattedMessage id='Profile_Documents.regenerate' />,
                            disabled:
                                Object.keys(selectedDocumentsForRegeneration).length === 0 || !selectedRegenerateReason,
                            clickEvent: () => handleRegenerateDocuments(),
                        },
                    ]}
                />
            )}
        </Container>
    );
};

export default ProfileDocuments;
